import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useRowboat } from '@/rowboat';
import { getIdentifyQueue, type IdentifyQueue, type QueueSnapshot } from '@/services/identifyQueue';
import { createIdentifyRunner } from '@/services/identifyRunner';

// Timers can fire a few ms before Date.now() reaches the due time; without slack the job is skipped and never retried.
const DUE_SLACK_MS = 250;

const warnDrain = (err: unknown) => console.warn('identify drain failed', err);

// Kept outside the hook: RowboatProvider remounts the app when the identity changes.
const lastGraph = new WeakMap<IdentifyQueue, object>();

export function useIdentifyStatus(queue: IdentifyQueue = getIdentifyQueue()): QueueSnapshot {
  return useSyncExternalStore(queue.subscribe, queue.snapshot);
}

/** Mount once: drains on mount, on reconnect, on a new graph, and when the next job comes due. */
export function useIdentifyDrain(queue: IdentifyQueue = getIdentifyQueue()): void {
  const graph = useRowboat();
  const run = useMemo(() => createIdentifyRunner(graph), [graph]);
  const nextDueAt = useSyncExternalStore(queue.subscribe, () => queue.snapshot().nextDueAt);
  const drain = useCallback(() => {
    queue.drain(run).catch(warnDrain);
  }, [queue, run]);

  useEffect(() => {
    const previous = lastGraph.get(queue);
    lastGraph.set(queue, graph);
    // A new graph means a sign-in or sign-out, after which a 401 pause or backoff is stale.
    const ready = previous && previous !== graph ? queue.wake() : queue.refresh();
    ready.then(drain).catch(warnDrain);
    const onOnline = () => {
      queue.wake().then(drain).catch(warnDrain);
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [queue, graph, drain]);

  useEffect(() => {
    if (nextDueAt === undefined) return;
    const timer = setTimeout(drain, Math.max(0, nextDueAt - Date.now()) + DUE_SLACK_MS);
    return () => clearTimeout(timer);
  }, [nextDueAt, drain]);
}
